import type React from "react";
import {
  Button,
  ButtonClear,
  Icon,
  Text,
  type ButtonProps,
  type Omittable,
} from "@salestrak/ui";

// `value` is the chip's own prop, not the native form attribute.
type FilterValueChipProps = Omit<
  ButtonProps,
  "children" | "size" | "variant" | "expanded" | "loading" | "value"
> &
  Omittable<{
    /** The facet the chip filters — "Status", "Team", "Sort". Always on the
     *  face, so a row of chips reads as a row of questions. */
    label: string;
    /** The chosen value: plain text ("Online"), or an entity's visual (a
     *  team tile, a member avatar) for a pick the avatar names better than
     *  its label does. Absent while the facet is unfiltered. */
    value?: React.ReactNode;
    /** Defaults to `value != null`. A caller whose "set" state is not a
     *  value on the face — a sort that deviates from the default — passes
     *  it explicitly. */
    selected?: boolean;
    /** Renders the clear (×) affordance while selected. */
    onClear?: () => void;
    clearLabel?: string;
    size?: "xs" | "sm";
    ref?: React.Ref<HTMLButtonElement>;
  }>;

/**
 * THE labeled selector chip: `Label ⌄` at rest on the quiet outline, and
 * `Label: Value ⌄` on the primary-hued outline once a value is held, with
 * the hover-revealed × clearing it. The label stays on the face in both
 * states — unlike `FilterToggle`, whose label IS the selection — so a row of
 * these reads as the facets a list can be narrowed by, filled or not.
 *
 * Composes as the `render` child of a Menu / Popover / Select /
 * EntitySelector trigger: the trigger's own id, handlers and aria state
 * arrive in props and pass straight through to the Button.
 */
export function FilterValueChip({
  label,
  value,
  selected,
  onClear,
  clearLabel = "Clear",
  size = "xs",
  ref,
  ...props
}: FilterValueChipProps) {
  const isSelected = selected ?? value != null;
  return (
    <Button
      ref={ref}
      size={size}
      color={isSelected ? "primary" : "neutral"}
      variant="outline"
      // The clear disc reveals only under `data-expanded` (the FilterToggle
      // mechanic) — the face reserves no trailing room; the opaque disc
      // overlays the chevron on hover.
      expanded={isSelected && !!onClear}
      {...props}
    >
      {/* The label rides the chip's own type rung (12px on the 24px `xs`
          chip), so `size={null}` inherits it instead of pinning one size. */}
      <Text size={null} truncate={1}>
        {value != null ? `${label}:` : label}
      </Text>
      {value != null ? (
        typeof value === "string" ? (
          <Text size={null} truncate={1}>
            {value}
          </Text>
        ) : (
          value
        )
      ) : null}
      {/* Muted on the resting chip; inside the themed chip it inherits the
          brand ink so the chip reads as one piece. */}
      <Icon
        name="chevron-down"
        size="xs"
        color={isSelected ? undefined : "muted"}
      />
      {isSelected && onClear ? (
        <ButtonClear aria-label={clearLabel} onClick={() => onClear()} />
      ) : null}
    </Button>
  );
}
